import { backendApi } from "@/services/backend/api";
import {
  Contract,
  Lifetime,
  State,
} from "@/services/backend/rlt/contracts/types/contract";
import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";

const contractApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltContractCreate: build.mutation<Contract, CreateRequest>({
      query: (request) => ({
        url: `/rlt/contract`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "RltContract", id: "LIST" }],
    }),
    rltContractCreateForOffer: build.mutation<Contract, CreateForOfferRequest>({
      query: (request) => ({
        url: `/rlt/contract/for-offer`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [
        { type: "RltContract", id: "LIST" },
        "RltOffer",
        "RltContract",
        "RltPricingByArea",
      ],
    }),
    rltContractShow: build.query<Contract, ShowRequest>({
      query: (request) => ({
        url: "/rlt/contract",
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltContract", id: result.id }] : [],
    }),
    rltContractList: build.query<Lst<Contract>, Partial<ListRequest>>({
      query: (request) => ({
        url: "/rlt/contract/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((contract) => ({
                type: "RltContract" as const,
                id: contract.id,
              })),
              { type: "RltContract", id: "LIST" },
            ]
          : [{ type: "RltContract", id: "LIST" }],
    }),
    rltContractCopyFromOffer: build.mutation<Contract, CopyFromOfferRequest>({
      query: (request) => ({
        url: `/rlt/contract/copy-from-offer`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
        "RltCalculation",
        "RltTreatment",
        "RltPricingByArea",
      ],
    }),
    rltContractAddTreatmentCycle: build.mutation<
      Contract,
      AddTreatmentCycleRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/treatment-cycle`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
        "RltPricingByArea",
      ],
    }),
    rltContractRemoveTreatmentCycle: build.mutation<
      Contract,
      RemoveTreatmentCycleRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/treatment-cycle`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
        "RltPricingByArea",
      ],
    }),
    rltContractUpdateLifetime: build.mutation<Contract, UpdateLifetimeRequest>({
      query: (request) => ({
        url: `/rlt/contract/lifetime`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractRegisterScan: build.mutation<
      Contract,
      RegisterSignedContractRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/register-signed-contract`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractTerminate: build.mutation<Contract, TerminateRequest>({
      query: (request) => ({
        url: `/rlt/contract/terminate`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdateCustomerAddress: build.mutation<
      Contract,
      UpdateCustomerAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/customer-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdateServiceAddress: build.mutation<
      Contract,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdatePaymentTerms: build.mutation<
      Contract,
      UpdatePaymentTermsRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/payment-terms`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdateSpecialAgreements: build.mutation<
      Contract,
      UpdateSpecialAgreementsRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/special-agreements`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractPreviewPDF: build.query<
      { objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/rlt/contract`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractDownloadPDF: build.mutation<
      { filename: string; objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/rlt/contract`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractSetImmutable: build.mutation<Contract, SetImmutableRequest>({
      query: (request) => ({
        url: `/rlt/contract/immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
        "RltContract",
        "NumbersDocumentNumber",
      ],
    }),
  }),
});

export const {
  useRltContractCreateMutation,
  useRltContractCreateForOfferMutation,
  useRltContractShowQuery,
  useRltContractListQuery,
  useRltContractCopyFromOfferMutation,
  useRltContractAddTreatmentCycleMutation,
  useRltContractRemoveTreatmentCycleMutation,
  useRltContractUpdateLifetimeMutation,
  useRltContractRegisterScanMutation,
  useRltContractTerminateMutation,
  useRltContractUpdateCustomerAddressMutation,
  useRltContractUpdateServiceAddressMutation,
  useRltContractUpdatePaymentTermsMutation,
  useRltContractUpdateSpecialAgreementsMutation,
  useRltContractPreviewPDFQuery,
  useRltContractDownloadPDFMutation,
  useRltContractSetImmutableMutation,
} = contractApi;

export interface CreateRequest {
  id: string;
  processId?: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateForOfferRequest {
  id: string;
  processId?: string;
  offerId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  state?: Filter<State>;
  lifetimeStartDate?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface CopyFromOfferRequest {
  id: string;
  offerId: string;
}

export interface AddTreatmentCycleRequest {
  id: string;
  cycle: number;
}

export interface RemoveTreatmentCycleRequest {
  id: string;
  cycle: number;
}

export interface UpdateLifetimeRequest {
  id: string;
  lifetime: Lifetime;
}

export interface RegisterSignedContractRequest {
  id: string;
  file: File;
  dataFormat: string;
  scannedBy: string;
}

export interface TerminateRequest {
  id: string;
  terminationReason: string;
}

export interface UpdateCustomerAddressRequest {
  id: string;
  customerAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateTreatmentPriceRelativeDiscountRequest {
  id: string;
  active: boolean;
  percentage: number;
}

export interface UpdateTreatmentPriceAbsoluteDiscountRequest {
  id: string;
  active: boolean;
  amount: number;
}

export interface UpdatePricesForExtrasRequest {
  id: string;
  hourlyRate: number;
  ratePerAccessPanel: number;
}

export interface UpdatePaymentTermsRequest {
  id: string;
  terms: string;
}

export interface UpdateSpecialAgreementsRequest {
  id: string;
  agreements: string;
}

export interface SetImmutableRequest {
  id: string;
  reason: string;
  userId: string;
}

export interface PreviewPDFRequest {
  id: string;
  document: ContractDocument;
}

export enum ContractDocument {
  ALL = "all",
  CONTRACT = "contract",
  TREATMENT = "treatment",
  TREATMENT_BY_AREA = "treatment-by-area",
}
